import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import { FiPaperclip, FiPlus } from "react-icons/fi";
import { EmptyState, PageHeader, StatusBadge } from "./DashboardUI";
import Loader from "../loader/Loader";
import {
  createOperatorTickets,
  getOperatorTickets,
  getTicketTypes,
} from "../../redux/actions/TicketActions";
import { uploadTicketDocuments } from "../../redux/actions/DocumentActions";
import { getAllCompanies } from "../../redux/actions/CompanyActions";

const TICKET_STATUS = {
  initiated: "En attente",
  in_progress: "En cours",
  fulfilled: "Terminée",
  failed: "Échouée",
};

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

const EMPTY_FORM = { type: "", description: "", company: null };

// Operator page of a service: requests of all clients to this service
function OperatorServiceTickets({
  serviceCode,
  title,
  lead,
  fetchTickets,
  allowCreate = true,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { ticketTypes, operatorTickets } = useSelector(
    (state) => state.tickets,
  );
  const { userCompanies } = useSelector((state) => state.companies);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(
        fetchTickets
          ? fetchTickets(user.token, user.country)
          : getOperatorTickets(user.token, serviceCode, user.country),
      ),
      dispatch(getAllCompanies(user.token)),
    ]).finally(() => setLoading(false));
  }, [user?.token, user?.country, serviceCode, fetchTickets, dispatch]);

  const typeName = (code) =>
    (ticketTypes || []).find((item) => item.code === code)?.name || "Demande";
  const serviceTypes = (ticketTypes || []).filter(
    (item) => item.service === serviceCode,
  );

  const term = search.trim().toLowerCase();
  const tickets = (operatorTickets || [])
    .filter(
      (ticket) =>
        (!status || ticket.status === status) &&
        (!term ||
          ticket.reference?.toLowerCase().includes(term) ||
          ticket.company?.businessName?.toLowerCase().includes(term) ||
          typeName(ticket.reclamationCode).toLowerCase().includes(term)),
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const pending = (operatorTickets || []).filter(
    (ticket) => ticket.status === "initiated",
  ).length;

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setAttachment(null);
  };

  const handleCreated = async ({ success, message, data }) => {
    if (!success) {
      setSubmitting(false);
      toast("error", "Échec", message);
      return;
    }

    if (attachment) {
      const formData = new FormData();
      formData.append("documents", attachment);
      await dispatch(
        uploadTicketDocuments(user.token, data?.reference, formData, () => {}),
      );
    }

    setSubmitting(false);
    closeForm();
    toast("success", "Demande créée", message);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    dispatch(
      createOperatorTickets(
        user.token,
        {
          reclamationCode: form.type,
          company: form.company?.value,
          description: form.description,
        },
        serviceCode,
        user.country,
        handleCreated,
      ),
    );
  };

  return (
    <>
      <Loader isLoading={submitting} />
      <PageHeader
        title={title}
        lead={
          pending
            ? `${pending} demande${pending > 1 ? "s" : ""} en attente de traitement.`
            : lead
        }
        actions={
          allowCreate && (
            <button
              type="button"
              className="dash-button"
              onClick={() => setShowForm(true)}
            >
              <FiPlus aria-hidden="true" /> Nouvelle demande
            </button>
          )
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence, une entreprise ou un type"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Statut"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(TICKET_STATUS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des demandes…</p>
        ) : tickets.length === 0 ? (
          <EmptyState title="Aucune demande">
            Aucune demande ne correspond à ces critères.
          </EmptyState>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table is-responsive">
              <thead>
                <tr>
                  <th>Demande</th>
                  <th>Entreprise</th>
                  <th>Référence</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.reference}>
                    <td>
                      <span className="dash-cell-title">
                        {typeName(ticket.reclamationCode)}
                      </span>
                    </td>
                    <td data-label="Entreprise">
                      {ticket.company?.businessName || "—"}
                    </td>
                    <td data-label="Référence">#{ticket.reference}</td>
                    <td data-label="Date">
                      {moment(ticket.createdAt).format("DD/MM/YYYY")}
                    </td>
                    <td data-label="Statut">
                      <StatusBadge
                        status={ticket.status}
                        label={TICKET_STATUS[ticket.status]}
                      />
                    </td>
                    <td>
                      <div className="dash-row-actions">
                        <button
                          type="button"
                          className="dash-link-button"
                          onClick={() =>
                            navigate("/operator/ticket-details", {
                              state: { ticket },
                            })
                          }
                        >
                          Ouvrir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showForm} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="dash-card-title mb-0">
            Nouvelle demande pour une entreprise
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="dash-form" onSubmit={handleSubmit}>
            <label htmlFor="ticket-company">Entreprise</label>
            <Select
              inputId="ticket-company"
              options={(userCompanies || []).map((company) => ({
                value: company._id,
                label: company.businessName,
              }))}
              value={form.company}
              onChange={(company) => setForm({ ...form, company })}
              placeholder="Rechercher une entreprise"
              noOptionsMessage={() => "Aucune entreprise"}
            />

            <label htmlFor="ticket-type">Type de demande</label>
            <select
              id="ticket-type"
              value={form.type}
              onChange={(event) =>
                setForm({ ...form, type: event.target.value })
              }
              required
            >
              <option value="">Choisissez le type</option>
              {serviceTypes.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>

            <label htmlFor="ticket-description">Description</label>
            <textarea
              id="ticket-description"
              rows={4}
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              placeholder="Décrivez le besoin"
              required
            />

            <label htmlFor="ticket-file" className="dash-file">
              <FiPaperclip aria-hidden="true" />
              {attachment
                ? attachment.name
                : "Ajouter une pièce jointe (facultatif)"}
            </label>
            <input
              id="ticket-file"
              type="file"
              className="visually-hidden"
              onChange={(event) => setAttachment(event.target.files?.[0])}
            />

            <div className="dash-form-actions">
              <button
                type="button"
                className="dash-button is-secondary"
                onClick={closeForm}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="dash-button"
                disabled={submitting || !form.company}
              >
                Créer la demande
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OperatorServiceTickets;
